import { ButtonHTMLAttributes, FC, ReactNode } from 'react'
import classes from './index.module.css'
import cn from 'classnames'
import { LoadingOutlined } from '@ant-design/icons'

/** @ant-design/icons + React 18: лишние onPointer*Capture только в типах */
const LoadingIcon = LoadingOutlined as unknown as FC<{ className?: string }>

interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  color?: 'primary' | 'secondary' | 'gray' | 'dark-gray' | 'white' | 'danger' | 'yellow'
  variant?: 'default' | 'outline' | 'light' | 'action'
  rightContent?: ReactNode
  leftContent?: ReactNode
  isLoading?: boolean
  classNames?: {
    button?: string
    loader?: string
    rightContentWrapper?: string
    leftContentWrapper?: string
  }
}

const Button: FC<ButtonProps> = ({
  color = 'primary',
  variant = 'default',
  rightContent,
  leftContent,
  children,
  className,
  isLoading,
  style,
  classNames,
  ...props
}) => {
  const palette = {
    primary: 'var(--color-button-primary-bg)',
    hover: 'var(--color-button-primary-hover-bg)',
    text: 'var(--color-button-primary-text)',
  }

  const isAction = variant === 'action'
  const isActionAccent = isAction && color === 'secondary'

  let styleVar: Record<string, string> = {}

  if (isAction) {
    styleVar = {
      '--text-color': 'var(--color-button-strong-text)',
      '--background-color':
        color === 'secondary'
          ? 'var(--color-button-secondary-bg)'
          : 'var(--color-button-muted-bg)',
      '--hover-background-color': 'var(--color-button-secondary-hover-bg)',
    }
  }

  switch (color) {
    case 'primary':
      palette.text = 'var(--color-button-primary-text)'
      palette.primary = 'var(--color-button-primary-bg)'
      palette.hover = 'var(--color-button-primary-hover-bg)'
      break

    case 'secondary':
      palette.text = 'var(--color-button-strong-text)'
      palette.primary = 'var(--color-button-secondary-bg)'
      palette.hover = 'var(--color-button-secondary-hover-bg)'
      break

    case 'gray':
      palette.text = 'var(--color-button-strong-text)'
      palette.primary = 'var(--color-button-muted-bg)'
      palette.hover = 'var(--color-button-secondary-hover-bg)'
      break

    case 'dark-gray':
      palette.text = 'var(--color-button-primary-text)'
      palette.primary = 'var(--color-gray)'
      palette.hover = 'var(--color-text-neutral-soft)'
      break

    case 'white':
      palette.text = 'var(--color-button-strong-text)'
      palette.primary = 'var(--color-button-neutral-bg)'
      palette.hover = 'var(--color-button-neutral-hover-bg)'
      break
    case 'yellow':
      palette.text = 'var(--color-button-warning-text)'
      palette.primary = 'var(--color-button-warning-bg)'
      palette.hover = 'var(--color-button-warning-hover-bg)'
      break

    case 'danger':
      palette.text = 'var(--color-button-danger-text)'
      palette.primary = 'var(--color-button-danger-bg)'
      palette.hover = 'var(--color-button-danger-hover-bg)'
      break

    default:
      break
  }

  if (!isAction) {
    switch (variant) {
      case 'default':
        styleVar = {
          '--text-color': palette.text,
          '--background-color': palette.primary,
          '--hover-background-color': palette.hover,
        }
        break

      case 'outline':
        styleVar = {
          '--text-color': palette.primary,
          '--background-color': 'transparent',
          '--border-color': palette.primary,
          '--border-type': 'solid',
          '--border-width': 'calc(0.0625 * var(--adapt-rem))', // 1px
          '--hover-background-color': 'transparent',
        }
        break

      case 'light':
        styleVar = {
          '--text-color': palette.primary,
          '--background-color': `color-mix(in srgb, ${palette.primary} 16%, transparent)`,
          '--hover-background-color': `color-mix(in srgb, ${palette.primary} 24%, transparent)`,
        }
        break
    }
  }

  return (
    <button
      type="button"
      className={cn(
        classes.button,
        isAction && classes.button_action,
        isActionAccent && classes.button_actionAccent,
        classNames?.button,
        className,
      )}
      style={{ ...styleVar, ...style }}
      {...props}
    >
      <>
        {leftContent && (
          <div
            className={cn(
              isAction && classes.actionIconSlot,
              classNames?.leftContentWrapper,
            )}
          >
            {leftContent}
          </div>
        )}
        {isLoading && (
          <LoadingIcon className={cn(classes.loadingIcon, classNames?.loader)} />
        )}
        {children}
        {rightContent && (
          <div className={classNames?.rightContentWrapper}>{rightContent}</div>
        )}
      </>
    </button>
  )
}

export default Button
